import { useApplicationByEdition } from "@/hooks/application-by-edition.hook";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useResponsesByQuizId } from "@/hooks/responses-by-quiz-id.hook";
import { apiErrorToToast } from "@/lib/utils";
import { currentEditionIdAtom } from "@/store/edition-id.store";
import { QuestionProvider } from "@/store/question.context";
import { ApiErrorResponse } from "@/types/apis";
import { DimensionId, Topic } from "@/types/questionnaries";
import { AxiosError } from "axios";
import { useAtomValue } from "jotai";
import { AnimatePresence, motion } from "motion/react";
import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import QuestionCard from "./question-card/question-card";
import TopicHeader from "./topic-header";

type TopicAccordionItemProps = {
  topic: Topic;
  order: number;
  disabled?: boolean;
  showResults?: boolean;
  showEvidences?: boolean;
};

const TopicAccordionItem: FC<TopicAccordionItemProps> = ({
  topic,
  order,
  disabled,
  showResults = false,
  showEvidences,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Internal state to disable the topic while it's being animated
  // so answers are not saved when rendering
  const [isDisabled, setIsDisabled] = useState(!!disabled);

  const editionId = useAtomValue(currentEditionIdAtom);

  const { dimensionId } = useParams();

  const { data: userProfile } = useCurrentUserProfile();

  const { data: application } = useApplicationByEdition({
    companyId: userProfile?.companyId || "",
    editionId,
  });

  const quizId = application?.quizId || "";

  const {
    data: questions,
    isLoading,
    error,
  } = useResponsesByQuizId(
    {
      companyId: userProfile?.companyId || "",
      quizId,
      dimension: dimensionId as DimensionId,
      topic: topic.topic,
    },
    isOpen,
  );

  const showContent = isOpen && !isLoading;

  useEffect(() => {
    if (error) {
      apiErrorToToast(error as AxiosError<ApiErrorResponse>);
      setIsOpen(false);
    }
  }, [error]);

  useEffect(() => {
    setIsDisabled(!!disabled);
  }, [disabled]);

  if (!quizId) {
    return null;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <TopicHeader
        topic={topic}
        order={order}
        isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        showLoader={isLoading}
        showWeight={dimensionId !== "COMPANY_INFORMATION"}
        quizId={quizId}
        dimensionId={dimensionId as DimensionId}
      />

      <AnimatePresence>
        {showContent && (
          <motion.div
            initial={{ height: 0, translateY: -10, opacity: 0 }}
            animate={{
              height: showContent ? "auto" : 0,
              translateY: 0,
              opacity: 1,
            }}
            exit={{ height: 0, translateY: -10, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="m-4 mt-2 flex flex-col gap-4"
            onAnimationStart={() => setIsDisabled(true)}
            onAnimationComplete={() => setIsDisabled(!!disabled)}
          >
            {questions?.map((question, index) => (
              <QuestionProvider
                key={question.id}
                responseId={question.responseId}
                quizId={quizId}
              >
                <QuestionCard
                  question={question}
                  topicOrder={order}
                  questionOrder={index + 1}
                  disabled={isDisabled}
                  showResults={showResults}
                  showEvidences={showEvidences}
                />
              </QuestionProvider>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TopicAccordionItem;
